import cx from 'classnames'
import { ChipItem, ChipSelect } from 'components/ChipSelect/ChipSelect'
import { Selection } from '@react-types/shared'
import { VoiceData } from 'hooks/useBrandVoices'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { BrandVoice, Settings, Add } from 'almanac-ui/icons'
import { IconButton } from 'components/IconButton/IconButton'
import Styles from './VoiceSelect.module.scss'
import { useModals } from 'almanac-ui/Modal'
import { EditVoiceModal } from 'screens/BrandKits/components/BrandVoice/EditVoiceModal'
import { Chip } from 'components/Chip/Chip'
import { useCampaignCreationContext } from 'components/CampaignCreationModal/CampaignCreationModal'
import { hexToRgbaString } from 'utils/colorHelpers'
import { MAX_INITIAL_PILLS } from './VoicesAndTonesSelect'
import { useOpenBrandVoiceModal } from 'screens/BrandKits/components/BrandVoice/useOpenBrandVoiceModal'
import { CreateContentModal } from 'screens/BrandKits/components/BrandVoice/AddVoice/CreateContentModal'

interface Props {
  onChange: (voice?: number) => void
  selectedVoice?: number
  inline?: boolean
  showAll?: boolean
  voices: VoiceData[]
}

export const VoiceSelect = ({ onChange, selectedVoice, inline, voices, showAll }: Props) => {
  const currentWorkspace = useCurrentWorkspace()
  const { openModal } = useModals()
  const { setExpanded, handleSelectVoice } = useCampaignCreationContext()
  const openBrandVoiceModal = useOpenBrandVoiceModal()

  const handleChange = (selection: Selection) => {
    if (!(selection instanceof Set)) return

    const [voice] = selection
    onChange(voice ? Number(voice) : undefined)
  }

  const selectedKeys = selectedVoice ? [selectedVoice.toString()] : []

  const openAddVoiceModal = () => {
    if (!currentWorkspace) return

    openBrandVoiceModal(
      {
        onComplete: (voiceId?: number) => {
          setExpanded(2)
          handleSelectVoice(voiceId)
        },
      },
      { newStack: true },
    )
  }

  const openEditVoiceModal = (id: number) => {
    const voiceData = voices.find((v) => v.id === id)

    if (!voiceData || !currentWorkspace) return
    openModal(
      EditVoiceModal,
      {
        voiceData: voiceData,
        onCreate: (createProps) => openModal(CreateContentModal, createProps),
      },
      { newStack: true },
    )
  }

  // TODO: We need to revisit this
  //       Warning: validateDOMNesting(...): <button> cannot appear as a descendant of <button>
  //       And testing this is harder than other ChipSelects
  return (
    <>
      <ChipSelect
        selectedKeys={selectedKeys}
        selectionMode="single"
        variant="wizard"
        deletable={false}
        onSelectionChange={handleChange}
        inline={inline}
      >
        {voices.slice(0, showAll ? voices.length : MAX_INITIAL_PILLS).map((v) => {
          return (
            <ChipItem key={v.id} textValue={v.voice_params.name} color={hexToRgbaString(v.color, 0.3)}>
              <div className={Styles.voiceContainer}>
                {v.voice_params.emoji ? <span>{v.voice_params.emoji}</span> : <BrandVoice />}
                {v.voice_params.name}
                <IconButton
                  className={cx(Styles.editIcon, { [Styles.selected]: selectedVoice === v.id })}
                  onPress={() => openEditVoiceModal(v.id)}
                  variant="tertiary"
                  size="sm"
                  icon={Settings}
                />
              </div>
            </ChipItem>
          )
        })}
      </ChipSelect>
      <Chip
        variant="wizard"
        className={Styles.createBrandVoice}
        onPress={openAddVoiceModal}
        deletable={false}
        aria-label="Create brand voice"
      >
        <div className={Styles.voiceContainer} data-onboarding-step-id="create-brand-voice">
          <Add />
          Create Brand Voice
        </div>
      </Chip>
    </>
  )
}
